import './styles/RemovePieceStyle.css';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  BaseInput,
  LockerColumn,
  NavigationButton,
  ToastType,
  TopNav,
  TopNavToastMessage,
} from '../common';
import { languageMap } from '../../maps/languageMap';
import { useActions, useTypedSelector } from '../../hooks';
import { APIResponseStatusType, navigationPages } from '../../models/types';
import { useHistory } from 'react-router-dom';
import {
  LockerColumnStatuses,
  LockerColumnTypes,
  LockerPosition,
  LockerPositionStatus,
  LockerPositionType,
} from '../../models/microservices';
import { apiErrorHandler, apiPOST } from '../../restApi';
import {
  ExitConfirmationRequestPOST,
  ExitConfirmationResponsePOST,
} from '../../models/cruds';
import Spinner from 'react-bootstrap/Spinner';
import { Lock } from '../../devices/locker/interface/Locker/Lock';

const EXIT_PIECE_ERROR =
  'Error en la consulta, no pudimos retirar la pieza en CRA';
const REMOVING_PIECE_API = 'Confirmando el retiro en CRA';
const API_VALIDATION_ERROR = 'Error en la consulta';
const LOCKER_MESSAGE_OPENING = '¡Abriendo locker!';
const LOCKER_MESSAGE_OPENED =
  'Puedes retirar el paquete, por favor cierra la puerta, ¡Muchas gracias!';
const LOCKER_MESSAGE_CLOSED =
  'Paquete retirado, por favor confirma para continuar, ¡Muchas gracias!';

const PRIMARY_COLUMN_COMPARTMENT_LENGTH = 8;
const PRIMARY_MASTER_COMPARTMENT_INDEX = 1;
const PRIMARY_MASTER_COMPARTMENT = {
  lockerPositionType: {
    name: LockerColumnTypes.SCREEN,
  } as LockerPositionType,
  lockerPositionStatus: {
    name: LockerColumnStatuses.BLOCKED,
  } as LockerPositionStatus,
} as LockerPosition;

const RemovePiece: React.FC = () => {
  let history = useHistory();
  const removeLockRef = useRef<Lock>();
  const lockerPositionRef = useRef<LockerPosition>();
  const [removeLock, setRemoveLock] = useState<Lock>();
  const [loading, setLoading] = useState(false);
  const [lockOpened, setLockOpened] = useState(false);
  const [instructions, setInstructions] = useState('');

  const [feedbackMessage, setFeedbackMessage] = useState('');
  const language = useTypedSelector(({ session }) => {
    return session.language;
  });
  const agentToken = useTypedSelector(({ session }) => {
    return session.dhlAgentToken;
  });
  const currentLockerPosition = useTypedSelector(({ locker }) => {
    return locker.currentLockerPosition;
  });
  const currentPackageCandidate = useTypedSelector(({ locker }) => {
    return locker.currentPackageCandidate;
  });
  const lockerRemoveList = useTypedSelector(({ locker }) => {
    return locker.lockerRemoveList;
  });
  const lockerContent = useTypedSelector(({ locker }) => {
    return locker.lockerContent;
  });
  const locker = useTypedSelector(({ devices }) => {
    return devices.locker;
  });

  const {
    updateRemoveLockerList,
    updateCurrentLockerPosition,
    updateCurrentPackageCandidate,
  } = useActions();
  removeLockRef.current = removeLock;
  lockerPositionRef.current = currentLockerPosition;
  //On Loading
  useEffect(() => {
    const addLockerEventListeners = () => {
      locker?.addEventListener('lockOpened', onLockOpened as EventListener);
      locker?.addEventListener('lockClosed', onLockClosed as EventListener);
    };
    console.log('on loading');
    addLockerEventListeners();
  }, [locker]);
  //On unloading
  useEffect(
    () => () => {
      console.log('on unloading');
      removeLockerEventListeners();
    },
    []
  );

  const removeLockerEventListeners = () => {
    locker?.removeEventListener('lockOpened', onLockOpened as EventListener);
    locker?.removeEventListener('lockClosed', onLockClosed as EventListener);
  };
  const onLockOpened = useCallback(
    (event: CustomEvent) => {
      const openedLock = event.detail as Lock;

      console.log(
        'lock Opened',
        event.detail,
        removeLockRef.current,
        openedLock
      );
      if (
        openedLock.sid === removeLockRef.current?.sid &&
        openedLock.cid === removeLockRef.current?.cid
      ) {
        console.log('lock Opened true', event.detail);
        setInstructions(LOCKER_MESSAGE_OPENED);
        setLockOpened(true);
      }
    },
    [removeLock]
  );
  const onLockClosed = useCallback(
    (event: CustomEvent) => {
      const closedLock = event.detail as Lock;
      console.log(
        'lock Closed ',
        removeLockRef.current,
        closedLock,
        lockerPositionRef.current
      );
      if (
        closedLock.sid === removeLockRef.current?.sid &&
        closedLock.cid === removeLockRef.current?.cid
      ) {
        console.log('lock Closed true', event.detail);
        let newLockerPosition: LockerPosition = {
          ...lockerPositionRef.current,
          retrieved: true,
        } as LockerPosition;
        updateCurrentLockerPosition(newLockerPosition);
        setInstructions(LOCKER_MESSAGE_CLOSED);
        setLockOpened(false);
      }
    },
    [removeLock]
  );
  useEffect(() => {
    console.log(lockerRemoveList);

    if (lockerRemoveList.length !== 0) {
      let nextLockerPosition = lockerRemoveList.find(
        (position) => !position.retrieved
      );
      if (nextLockerPosition && nextLockerPosition.packageCandidate) {
        console.log('Updating next locker position', nextLockerPosition);
        updateCurrentLockerPosition(nextLockerPosition);
        updateCurrentPackageCandidate(nextLockerPosition.packageCandidate);
      }
    }
  }, [
    lockerRemoveList,
    updateCurrentLockerPosition,
    updateCurrentPackageCandidate,
  ]);
  useEffect(() => {
    if (currentLockerPosition.extraFields && !currentLockerPosition.retrieved) {
      setInstructions(LOCKER_MESSAGE_OPENING);
      setRemoveLock(
        new Lock(
          currentLockerPosition.extraFields.sid,
          currentLockerPosition.extraFields.cid
        )
      );
    }
  }, [currentLockerPosition]);
  useEffect(() => {
    if (locker && removeLock?.sid && removeLock?.cid) {
      locker.openLockAsync(removeLock);
    }
  }, [locker, removeLock]);
  useEffect(() => {
    if (lockOpened && !currentLockerPosition.retrieved) {
      console.log('Closing lock');
    }
  }, [lockOpened, currentLockerPosition, updateCurrentLockerPosition]);

  const removePieceAsync = async (): Promise<boolean> => {
    setLoading(true);
    setInstructions(REMOVING_PIECE_API);
    let packageCandidateId = currentPackageCandidate.id ?? 0;
    let lockerPositionId = currentLockerPosition.id;
    let request = new ExitConfirmationRequestPOST();
    let response: ExitConfirmationResponsePOST = await apiPOST(
      request.buildURL(),
      request.generateContent(packageCandidateId, lockerPositionId),
      request.generateConfig(agentToken)
    );
    let errorMsg = '';
    try {
      if (response.status === APIResponseStatusType.SUCCESS) {
        if (response.data.exitConfirmation) {
          setLoading(false);
          return true;
        } else {
          //TODO check for unauth and refresh token
          errorMsg = 'Unauthorized';
        }
      } else {
        errorMsg = apiErrorHandler(response);
      }
    } catch (err: any) {
      errorMsg = err.toString();
    }
    console.log(errorMsg);
    setFeedbackMessage(API_VALIDATION_ERROR);
    setLoading(false);
    return false;
  };
  const handleNextButton = async () => {
    let piecesRemaining = lockerRemoveList.filter(
      (position) => position.retrieved
    );
    if (piecesRemaining.length < lockerRemoveList.length) {
      if (await removePieceAsync()) {
        console.log('Updating Locker list');

        //Updating removed element retrieved property
        updateRemoveLockerList(
          lockerRemoveList.map((item) =>
            item.id === currentLockerPosition.id
              ? { ...item, retrieved: true }
              : item
          )
        );
        return;
      }
      setFeedbackMessage(EXIT_PIECE_ERROR);
      return;
    }
    history.replace(`/${navigationPages.AGENT_PROCESS_SELECTION}`);
  };
  const renderLockerColumn = () => {
    if (loading) {
      return (
        <div className="insert-piece-locker-container">
          <div className="spinner-container">
            <Spinner className="spinner" animation="grow" variant="danger" />
          </div>
        </div>
      );
    }
    if (currentLockerPosition?.extraFields) {
      let currentColumn = lockerContent
        .filter(
          (positions) =>
            positions.extraFields.sid === currentLockerPosition.extraFields.sid
        )
        .sort((a, b) => a.row.localeCompare(b.row));
      if (currentColumn.length === PRIMARY_COLUMN_COMPARTMENT_LENGTH) {
        currentColumn.splice(
          PRIMARY_MASTER_COMPARTMENT_INDEX,
          0,
          PRIMARY_MASTER_COMPARTMENT
        );
      }
      return (
        <div className="insert-piece-locker-container">
          <LockerColumn
            onSelectCompartment={(position) => {}}
            lockerColumn={currentColumn}
            currentCompartment={currentLockerPosition}
          />
        </div>
      );
    }
  };
  const renderContent = () => {
    return (
      <div className="locker-content-container">
        <div className="locker-content-container-left">
          <div className="insert-piece-input-container">
            <div className="insert-piece-awb-container">
              <BaseInput
                val={currentPackageCandidate.pieceNumber}
                inputLabel="Número de pieza"
                inputPlaceholder=""
                inError={false}
                maxLength={25}
                onTextChange={() => {}}
                extraClassContainer="no-padding-right full-width stretched"
                extraClass="placeholder-left"
              />
            </div>
            <div className="insert-piece-awb-container">
              <BaseInput
                val={currentPackageCandidate.awbNumber}
                inputLabel="Número de guía"
                inputPlaceholder=""
                inError={false}
                maxLength={25}
                onTextChange={() => {}}
                extraClassContainer="no-padding-right full-width stretched"
                extraClass="placeholder-left"
              />
            </div>
            <div className="insert-piece-awb-container">
              <p className="remove-piece-awb-container">{instructions}</p>
            </div>
          </div>
        </div>
        <div className="locker-content-container-right">
          {renderLockerColumn()}
        </div>
      </div>
    );
  };
  const getNextButtonText = () => {
    let piecesRemaining = lockerRemoveList.filter(
      (position) => !position.retrieved
    );
    if (piecesRemaining.length > 1) {
      return languageMap.remove_piece.next_button[language];
    } else if (piecesRemaining.length === 1) {
      return languageMap.remove_piece.confirm_button[language];
    }
    return languageMap.remove_piece.finish_button[language];
  };
  const renderNextPieceButton = () => {
    if (currentLockerPosition.retrieved) {
      return (
        <NavigationButton
          buttonContent={getNextButtonText()}
          buttonClass="navigation-button confirm-button"
          onClickButton={() => {
            handleNextButton();
          }}
        />
      );
    }
  };
  const getRemainingPieces = (): number => {
    let piecesRemaining = lockerRemoveList.filter(
      (position) => position.retrieved
    );
    if (piecesRemaining) {
      return piecesRemaining.length + 1;
    }
    return 0;
  };
  const renderRemoveListCounter = () => {
    let piecesRemaining = lockerRemoveList.some(
      (position) => !position.retrieved
    );
    if (piecesRemaining) {
      return (
        <p className="remove-piece-piece-counter-text">
          {getRemainingPieces()} / {lockerRemoveList.length}
        </p>
      );
    }
    return null;
  };

  return (
    <div id="removePiece" className="main-container">
      <TopNav dataCyPrefix="scan-awb" />
      <div className="content-container">
        <div className="main-content-container fullpage">
          <TopNavToastMessage
            toastText={feedbackMessage}
            toastType={ToastType.ERROR}
            resetMessage={() => setFeedbackMessage('')}
          />
          <div className="title-content-container">
            <p className="header nopadding nomargin">
              {languageMap.remove_piece.title[language]}
            </p>
            {renderRemoveListCounter()}
          </div>
          <div className="data-content-container">{renderContent()}</div>
          <div className="footer-container">
            <div className="footer-button-container">
              <NavigationButton
                buttonContent={languageMap.remove_piece.cancel_button[language]}
                buttonClass="navigation-button cancel-button"
                onClickButton={() => {
                  history.replace(
                    `/${navigationPages.REMOVE_PROCESS_SELECTION}`
                  );
                }}
              />
            </div>
            <div className="footer-button-container">
              <NavigationButton
                buttonContent={languageMap.remove_piece.report_button[language]}
                buttonClass="navigation-button cancel-button"
                onClickButton={() => {
                  history.replace(`/${navigationPages.REMOVE_ISSUES}`);
                }}
              />
            </div>
            <div className="footer-button-container">
              {renderNextPieceButton()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { RemovePiece };
